import { useState } from 'react';
import { ModuleLink as Link } from '../../components/ModuleLink';
import Layout from '../../components/Layout';
import PeoplePicker from '../../components/PeoplePicker';
import Modal from '../../components/Modal';
import { useAuth } from '../../context/AuthContext';
import { useApiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import type { GraphUser, AffectedStakeholder } from '../../types';

// ── Form progress indicator ────────────────────────────────────────────────────
const SECTIONS = [
  'Change Details',
  'Description',
  'Risk',
  'Dependencies',
  'External & Policy',
  'Stakeholders',
] as const;

function FormProgress({ current }: { current: number }) {
  return (
    <div className='form-progress' aria-label='Form sections'>
      {SECTIONS.map((title, i) => {
        const step = i + 1;
        const isDone = step < current;
        const isActive = step === current;
        return (
          <div
            key={title}
            className={`form-step${isDone ? ' is-done' : isActive ? ' is-active' : ''}`}
          >
            <div className='form-step__indicator'>
              <div className='form-step__num'>
                {isDone ? (
                  <i className='fa-solid fa-check' aria-hidden='true' />
                ) : (
                  step
                )}
              </div>
              <span className='form-step__title'>{title}</span>
            </div>
            {i < SECTIONS.length - 1 && (
              <div className='form-step__connector' />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Single-select person pill ─────────────────────────────────────────────────
function PersonPill({
  user,
  onRemove,
}: {
  user: GraphUser;
  onRemove: () => void;
}) {
  return (
    <div className='user-pill'>
      <div className='user-pill__avatar'>
        {user.displayName.charAt(0).toUpperCase()}
      </div>
      <span className='user-pill__name'>{user.displayName}</span>
      <span className='user-pill__dept'>
        {user.department ?? user.userPrincipalName}
      </span>
      <button
        className='user-pill__remove'
        onClick={onRemove}
        aria-label={`Remove ${user.displayName}`}
      >
        <i className='fa-solid fa-xmark' aria-hidden='true' />
      </button>
    </div>
  );
}

// ── Scope-aware approver hint ─────────────────────────────────────────────────
const SCOPE_HINTS: Record<string, string> = {
  'Single Dept': 'Nominate the Department Head',
  'Multi Dept': 'Nominate the relevant Functional Head(s)',
  'Org-Wide': 'Nominate the MD/CEO or Executive Management',
};

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({
  step,
  total,
  title,
  icon,
  children,
  onBack,
  onNext,
  nextLabel = 'Next',
  nextDisabled,
}: {
  step: number;
  total: number;
  title: string;
  icon: string;
  children: React.ReactNode;
  onBack?: () => void;
  onNext: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
}) {
  return (
    <div className='card'>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          paddingBottom: 20,
          marginBottom: 24,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div>
          <p
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--primary)',
              marginBottom: 4,
            }}
          >
            Section {step} of {total}
          </p>
          <h2>{title}</h2>
        </div>
        <i
          className={`fa-solid ${icon}`}
          style={{
            fontSize: '1.5rem',
            color: 'var(--primary-light)',
            opacity: 0.6,
            marginTop: 4,
          }}
          aria-hidden='true'
        />
      </div>

      <div>{children}</div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 10,
          marginTop: 28,
          paddingTop: 20,
          borderTop: '1px solid var(--border)',
        }}
      >
        {onBack && (
          <button type='button' className='btn btn--secondary' onClick={onBack}>
            <i className='fa-solid fa-arrow-left' aria-hidden='true' /> Back
          </button>
        )}
        <button
          type='button'
          className='btn btn--primary'
          onClick={onNext}
          disabled={nextDisabled}
        >
          {nextLabel}{' '}
          {nextLabel === 'Next' && (
            <i className='fa-solid fa-arrow-right' aria-hidden='true' />
          )}
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SubmitChange() {
  const { userName, userEmail } = useAuth();
  const api = useApiClient();
  const { showToast } = useToast();

  const [currentSection, setCurrentSection] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [newChangeId, setNewChangeId] = useState('');

  // ── Section 1 state ──────────────────────────────────────────────────────
  const [changeName, setChangeName] = useState('');
  const [sponsor, setSponsor] = useState<GraphUser | null>(null);
  const [approver, setApprover] = useState<GraphUser | null>(null);
  const [approvers, setApprovers] = useState<GraphUser[]>([]);
  const [goLiveDate, setGoLiveDate] = useState('');
  const [scope, setScope] = useState('');
  const [s1Errors, setS1Errors] = useState<Record<string, string>>({});

  // ── Section 2 state ──────────────────────────────────────────────────────
  const [currentState, setCurrentState] = useState('');
  const [newState, setNewState] = useState('');
  const [benefitsReason, setBenefitsReason] = useState('');
  const [s2Errors, setS2Errors] = useState<Record<string, string>>({});

  // ── Section 3 state ──────────────────────────────────────────────────────
  const [riskImpl, setRiskImpl] = useState('');
  const [riskNoImpl, setRiskNoImpl] = useState('');
  const [s3Errors, setS3Errors] = useState<Record<string, string>>({});

  // ── Section 4 state ──────────────────────────────────────────────────────
  const [dependencies, setDependencies] = useState('');

  // ── Section 5 state ──────────────────────────────────────────────────────
  const [externalStakeholders, setExternalStakeholders] = useState('');
  const [policyUpdate, setPolicyUpdate] = useState('');
  const [policyDocs, setPolicyDocs] = useState('');
  const [s5Errors, setS5Errors] = useState<Record<string, string>>({});

  // ── Section 6 state ──────────────────────────────────────────────────────
  const [stakeholders, setStakeholders] = useState<AffectedStakeholder[]>([]);
  const [s6Error, setS6Error] = useState('');

  // ── Validation ───────────────────────────────────────────────────────────
  function validateS1() {
    const errs: Record<string, string> = {};
    if (!changeName.trim()) errs.changeName = 'Change name is required.';
    if (!sponsor) errs.sponsor = 'Change sponsor is required.';
    if (!scope) errs.scope = 'Please select a scope.';
    if (scope === 'Multi Dept') {
      if (approvers.length === 0)
        errs.approver = 'At least one approver is required.';
    } else {
      if (!approver) errs.approver = 'Approver is required.';
    }
    if (!goLiveDate || new Date(goLiveDate) <= new Date())
      errs.goLive = 'A future go-live date is required.';
    setS1Errors(errs);
    return Object.keys(errs).length === 0;
  }
  function validateS2() {
    const errs: Record<string, string> = {};
    if (!currentState.trim()) errs.currentState = 'Required.';
    if (!newState.trim()) errs.newState = 'Required.';
    if (!benefitsReason.trim()) errs.benefitsReason = 'Required.';
    setS2Errors(errs);
    return Object.keys(errs).length === 0;
  }
  function validateS3() {
    const errs: Record<string, string> = {};
    if (!riskImpl.trim()) errs.riskImpl = 'Required.';
    if (!riskNoImpl.trim()) errs.riskNoImpl = 'Required.';
    setS3Errors(errs);
    return Object.keys(errs).length === 0;
  }
  function validateS5() {
    const errs: Record<string, string> = {};
    if (!policyUpdate) errs.policyUpdate = 'Please select an option.';
    if (policyUpdate === 'Yes' && !policyDocs.trim())
      errs.policyDocs = 'Please list the documents to be updated.';
    setS5Errors(errs);
    return Object.keys(errs).length === 0;
  }
  function validateS6() {
    if (stakeholders.length === 0) {
      setS6Error('At least one stakeholder is required.');
      return false;
    }
    setS6Error('');
    return true;
  }

  // ── Navigation ────────────────────────────────────────────────────────────
  function next(validate: () => boolean, to: number) {
    if (validate()) setCurrentSection(to);
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  async function handleSubmit() {
    if (!validateS6()) return;
    setSubmitting(true);
    try {
      const approverList =
        scope === 'Multi Dept'
          ? approvers.map((a) => ({
              name: a.displayName,
              email: a.userPrincipalName,
            }))
          : [
              {
                name: approver!.displayName,
                email: approver!.userPrincipalName,
              },
            ];

      const payload = {
        changeName: changeName.trim(),
        sponsorName: sponsor!.displayName,
        sponsorEmail: sponsor!.userPrincipalName,
        approvers: JSON.stringify(approverList),
        scope,
        proposedGolive: goLiveDate,
        leadName: userName,
        leadEmail: userEmail,
        currentState: currentState.trim(),
        newState: newState.trim(),
        benefitsReason: benefitsReason.trim(),
        riskIfImplemented: riskImpl.trim(),
        riskIfNotImplemented: riskNoImpl.trim(),
        dependencies: dependencies.trim() || null,
        externalStakeholders: externalStakeholders.trim() || null,
        policyDocsUpdateRequired: policyUpdate as 'Yes' | 'No',
        policyDocsToUpdate: policyDocs.trim() || null,
        affectedStakeholders: JSON.stringify(stakeholders),
        status: 'Submitted',
        currentStep: 'Initiate',
        dateSubmitted: new Date().toISOString(),
      };
      const result = await api.post<{ changeId: string }>('/changes', payload);
      setNewChangeId(result.changeId);
      setSuccessModal(true);
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Approver helpers (multi-dept) ─────────────────────────────────────────
  function addApprover(user: GraphUser) {
    if (approvers.some((a) => a.userPrincipalName === user.userPrincipalName)) {
      showToast(`${user.displayName} is already added.`, 'warn');
      return;
    }
    setApprovers((prev) => [...prev, user]);
    setS1Errors((p) => ({ ...p, approver: '' }));
  }
  function removeApprover(idx: number) {
    setApprovers((prev) => prev.filter((_, i) => i !== idx));
  }

  // ── Stakeholder helpers ───────────────────────────────────────────────────
  function addStakeholder(user: GraphUser) {
    if (stakeholders.some((s) => s.email === user.userPrincipalName)) {
      showToast(`${user.displayName} is already added.`, 'warn');
      return;
    }
    setStakeholders((prev) => [
      ...prev,
      {
        name: user.displayName,
        email: user.userPrincipalName,
        department: user.department ?? '',
      },
    ]);
    setS6Error('');
  }
  function removeStakeholder(idx: number) {
    setStakeholders((prev) => prev.filter((_, i) => i !== idx));
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout maxWidth='form'>
      <div className='page-header'>
        <div className='page-header__title'>
          <div className='breadcrumb'>
            <Link to='/' className='breadcrumb__link'>
              <i className='fa-solid fa-house' aria-hidden='true' /> Home
            </Link>
            <span className='breadcrumb__sep'>/</span>
            <span className='breadcrumb__current'>New Change Request</span>
          </div>
          <h1>New Change Request</h1>
          <p>
            Complete all sections to submit your change for review and approval.
          </p>
        </div>
      </div>

      <FormProgress current={currentSection} />

      {/* ── Section 1 ── */}
      {currentSection === 1 && (
        <Section
          step={1}
          total={6}
          title='Change Details'
          icon='fa-file-pen'
          onNext={() => next(validateS1, 2)}
        >
          <div className='form-grid'>
            <div className='form-group form-group--full'>
              <label className='form-label' htmlFor='change-name'>
                Change Name <span className='required'>*</span>
              </label>
              <input
                id='change-name'
                type='text'
                className={`form-control${s1Errors.changeName ? ' is-error' : ''}`}
                value={changeName}
                onChange={(e) => setChangeName(e.target.value)}
                placeholder='Enter a clear, concise name for this change'
                maxLength={200}
              />
              {s1Errors.changeName && (
                <span className='form-error'>
                  <i
                    className='fa-solid fa-circle-exclamation'
                    aria-hidden='true'
                  />{' '}
                  {s1Errors.changeName}
                </span>
              )}
            </div>

            <div className='form-group'>
              <label className='form-label'>
                Change Sponsor <span className='required'>*</span>
              </label>
              {sponsor ? (
                <PersonPill user={sponsor} onRemove={() => setSponsor(null)} />
              ) : (
                <PeoplePicker
                  onSelect={(u) => {
                    setSponsor(u);
                    setS1Errors((p) => ({ ...p, sponsor: '' }));
                  }}
                  placeholder='Search by name or email…'
                />
              )}
              {s1Errors.sponsor && (
                <span className='form-error'>
                  <i
                    className='fa-solid fa-circle-exclamation'
                    aria-hidden='true'
                  />{' '}
                  {s1Errors.sponsor}
                </span>
              )}
            </div>

            <div className='form-group'>
              <label className='form-label' htmlFor='go-live-date'>
                Proposed Go-Live Date <span className='required'>*</span>
              </label>
              <input
                id='go-live-date'
                type='date'
                className={`form-control${s1Errors.goLive ? ' is-error' : ''}`}
                value={goLiveDate}
                onChange={(e) => setGoLiveDate(e.target.value)}
              />
              {s1Errors.goLive && (
                <span className='form-error'>
                  <i
                    className='fa-solid fa-circle-exclamation'
                    aria-hidden='true'
                  />{' '}
                  {s1Errors.goLive}
                </span>
              )}
            </div>

            <div className='form-group'>
              <label className='form-label'>
                Scope <span className='required'>*</span>
              </label>
              <div className='pill-group' role='radiogroup'>
                {['Single Dept', 'Multi Dept', 'Org-Wide'].map((s) => (
                  <button
                    key={s}
                    type='button'
                    className={`pill-option${scope === s ? ' is-selected' : ''}`}
                    onClick={() => {
                      setScope(s);
                      setS1Errors((p) => ({ ...p, scope: '' }));
                      // Reset approver state when switching scope
                      setApprover(null);
                      setApprovers([]);
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {s1Errors.scope && (
                <span className='form-error'>
                  <i
                    className='fa-solid fa-circle-exclamation'
                    aria-hidden='true'
                  />{' '}
                  {s1Errors.scope}
                </span>
              )}
            </div>

            <div className='form-group form-group--full'>
              <label className='form-label'>
                {scope === 'Multi Dept' ? 'Approvers' : 'Approver'}{' '}
                <span className='required'>*</span>
              </label>
              <p className='form-hint' style={{ marginBottom: 8 }}>
                {scope
                  ? SCOPE_HINTS[scope]
                  : 'Select a scope above to see approver guidance.'}
              </p>

              {scope === 'Multi Dept' ? (
                <>
                  <PeoplePicker
                    onSelect={addApprover}
                    placeholder='Search and add approvers…'
                  />
                  {approvers.length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        marginTop: 10,
                      }}
                    >
                      {approvers.map((a, idx) => (
                        <PersonPill
                          key={a.userPrincipalName}
                          user={a}
                          onRemove={() => removeApprover(idx)}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : approver ? (
                <PersonPill
                  user={approver}
                  onRemove={() => setApprover(null)}
                />
              ) : (
                <PeoplePicker
                  onSelect={(u) => {
                    setApprover(u);
                    setS1Errors((p) => ({ ...p, approver: '' }));
                  }}
                  placeholder='Search by name or email…'
                />
              )}
              {s1Errors.approver && (
                <span className='form-error'>
                  <i
                    className='fa-solid fa-circle-exclamation'
                    aria-hidden='true'
                  />{' '}
                  {s1Errors.approver}
                </span>
              )}
            </div>
          </div>
        </Section>
      )}

      {/* ── Section 2 ── */}
      {currentSection === 2 && (
        <Section
          step={2}
          total={6}
          title='Change Description'
          icon='fa-align-left'
          onBack={() => setCurrentSection(1)}
          onNext={() => next(validateS2, 3)}
        >
          <div className='form-grid'>
            {(
              [
                [
                  'current-state',
                  'Current State',
                  currentState,
                  setCurrentState,
                  s2Errors.currentState,
                  'How things work today — describe the existing process, system, or situation before this change is applied.',
                ],
                [
                  'new-state',
                  'New State',
                  newState,
                  setNewState,
                  s2Errors.newState,
                  'How things will work after the change — describe the target state once this change is implemented.',
                ],
                [
                  'benefits',
                  'Benefits / Reason for Change',
                  benefitsReason,
                  setBenefitsReason,
                  s2Errors.benefitsReason,
                  '',
                ],
              ] as const
            ).map(([id, label, val, setter, err, hint]) => (
              <div key={id} className='form-group form-group--full'>
                <label className='form-label' htmlFor={id}>
                  {label} <span className='required'>*</span>
                </label>
                {hint && (
                  <p className='form-hint' style={{ marginBottom: 6 }}>
                    {hint}
                  </p>
                )}
                <textarea
                  id={id}
                  className={`form-control${err ? ' is-error' : ''}`}
                  rows={4}
                  value={val}
                  onChange={(e) =>
                    (setter as React.Dispatch<React.SetStateAction<string>>)(
                      e.target.value,
                    )
                  }
                />
                {err && (
                  <span className='form-error'>
                    <i
                      className='fa-solid fa-circle-exclamation'
                      aria-hidden='true'
                    />{' '}
                    {err}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ── Section 3 ── */}
      {currentSection === 3 && (
        <Section
          step={3}
          total={6}
          title='Risk Consideration'
          icon='fa-shield-halved'
          onBack={() => setCurrentSection(2)}
          onNext={() => next(validateS3, 4)}
        >
          <div className='form-grid'>
            <div className='form-group form-group--full'>
              <label className='form-label' htmlFor='risk-impl'>
                What could go wrong if this change is implemented?{' '}
                <span className='required'>*</span>
              </label>
              <textarea
                id='risk-impl'
                className={`form-control${s3Errors.riskImpl ? ' is-error' : ''}`}
                rows={4}
                value={riskImpl}
                onChange={(e) => setRiskImpl(e.target.value)}
              />
              {s3Errors.riskImpl && (
                <span className='form-error'>
                  <i
                    className='fa-solid fa-circle-exclamation'
                    aria-hidden='true'
                  />{' '}
                  {s3Errors.riskImpl}
                </span>
              )}
            </div>
            <div className='form-group form-group--full'>
              <label className='form-label' htmlFor='risk-no-impl'>
                What could happen if this change is NOT implemented?{' '}
                <span className='required'>*</span>
              </label>
              <textarea
                id='risk-no-impl'
                className={`form-control${s3Errors.riskNoImpl ? ' is-error' : ''}`}
                rows={4}
                value={riskNoImpl}
                onChange={(e) => setRiskNoImpl(e.target.value)}
              />
              {s3Errors.riskNoImpl && (
                <span className='form-error'>
                  <i
                    className='fa-solid fa-circle-exclamation'
                    aria-hidden='true'
                  />{' '}
                  {s3Errors.riskNoImpl}
                </span>
              )}
            </div>
          </div>
        </Section>
      )}

      {/* ── Section 4 ── */}
      {currentSection === 4 && (
        <Section
          step={4}
          total={6}
          title='Dependencies & Assumptions'
          icon='fa-link'
          onBack={() => setCurrentSection(3)}
          onNext={() => setCurrentSection(5)}
        >
          <div className='form-group'>
            <label className='form-label' htmlFor='dependencies'>
              Dependencies, assumptions or constraints
              <span
                className='badge badge--muted'
                style={{ fontWeight: 400, marginLeft: 6 }}
              >
                Optional
              </span>
            </label>
            <textarea
              id='dependencies'
              className='form-control'
              rows={5}
              value={dependencies}
              onChange={(e) => setDependencies(e.target.value)}
              placeholder='List any systems, processes, teams or timelines this change depends on…'
            />
          </div>
        </Section>
      )}

      {/* ── Section 5 ── */}
      {currentSection === 5 && (
        <Section
          step={5}
          total={6}
          title='External Stakeholders & Policy Updates'
          icon='fa-users-gear'
          onBack={() => setCurrentSection(4)}
          onNext={() => next(validateS5, 6)}
        >
          <div className='form-grid'>
            <div className='form-group form-group--full'>
              <label className='form-label' htmlFor='ext-stakeholders'>
                External stakeholders affected
                <span
                  className='badge badge--muted'
                  style={{ fontWeight: 400, marginLeft: 6 }}
                >
                  Optional
                </span>
              </label>
              <textarea
                id='ext-stakeholders'
                className='form-control'
                rows={4}
                value={externalStakeholders}
                onChange={(e) => setExternalStakeholders(e.target.value)}
              />
            </div>

            <div className='form-group form-group--full'>
              <label className='form-label'>
                Does this change require policy or procedure updates?{' '}
                <span className='required'>*</span>
              </label>
              <div className='pill-group'>
                {['Yes', 'No'].map((v) => (
                  <button
                    key={v}
                    type='button'
                    className={`pill-option${policyUpdate === v ? ' is-selected' : ''}`}
                    onClick={() => {
                      setPolicyUpdate(v);
                      setS5Errors((p) => ({ ...p, policyUpdate: '' }));
                    }}
                  >
                    <i
                      className={`fa-solid ${v === 'Yes' ? 'fa-check' : 'fa-xmark'}`}
                      aria-hidden='true'
                    />{' '}
                    {v}
                  </button>
                ))}
              </div>
              {s5Errors.policyUpdate && (
                <span className='form-error'>
                  <i
                    className='fa-solid fa-circle-exclamation'
                    aria-hidden='true'
                  />{' '}
                  {s5Errors.policyUpdate}
                </span>
              )}
            </div>

            {policyUpdate === 'Yes' && (
              <div className='form-group form-group--full'>
                <label className='form-label' htmlFor='policy-docs'>
                  List documents to be updated{' '}
                  <span className='required'>*</span>
                </label>
                <textarea
                  id='policy-docs'
                  className={`form-control${s5Errors.policyDocs ? ' is-error' : ''}`}
                  rows={4}
                  value={policyDocs}
                  onChange={(e) => setPolicyDocs(e.target.value)}
                />
                {s5Errors.policyDocs && (
                  <span className='form-error'>
                    <i
                      className='fa-solid fa-circle-exclamation'
                      aria-hidden='true'
                    />{' '}
                    {s5Errors.policyDocs}
                  </span>
                )}
              </div>
            )}
          </div>
        </Section>
      )}

      {/* ── Section 6 ── */}
      {currentSection === 6 && (
        <Section
          step={6}
          total={6}
          title='Affected Stakeholders'
          icon='fa-user-group'
          onBack={() => setCurrentSection(5)}
          onNext={handleSubmit}
          nextLabel={submitting ? 'Submitting…' : 'Submit Change Request'}
          nextDisabled={submitting}
        >
          <p style={{ marginBottom: 16 }}>
            Add at least one affected stakeholder.
          </p>

          <PeoplePicker
            onSelect={addStakeholder}
            placeholder='Search to add a stakeholder…'
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginTop: 16,
            }}
          >
            {stakeholders.length === 0 ? (
              <div className='stakeholder-empty'>
                <i className='fa-solid fa-user-plus' aria-hidden='true' />
                <span>No stakeholders added yet.</span>
              </div>
            ) : (
              stakeholders.map((s, idx) => (
                <div key={s.email} className='stakeholder-row'>
                  <div className='stakeholder-row__avatar'>
                    {s.name.charAt(0).toUpperCase()}
                  </div>
                  <div className='stakeholder-row__info'>
                    <div className='stakeholder-row__name'>{s.name}</div>
                    <div className='stakeholder-row__meta'>
                      {s.email}
                      {s.department ? ` · ${s.department}` : ''}
                    </div>
                  </div>
                  <button
                    className='stakeholder-row__remove'
                    onClick={() => removeStakeholder(idx)}
                    aria-label={`Remove ${s.name}`}
                  >
                    <i className='fa-solid fa-trash-can' aria-hidden='true' />
                  </button>
                </div>
              ))
            )}
          </div>
          {s6Error && (
            <span className='form-error' style={{ marginTop: 12 }}>
              <i
                className='fa-solid fa-circle-exclamation'
                aria-hidden='true'
              />{' '}
              {s6Error}
            </span>
          )}
        </Section>
      )}

      {/* Success modal */}
      <Modal
        open={successModal}
        title='Change Request Submitted'
        footer={
          <Link to='/' className='btn btn--primary'>
            <i className='fa-solid fa-house' aria-hidden='true' /> Go to My
            Changes
          </Link>
        }
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            textAlign: 'center',
            padding: '8px 0',
          }}
        >
          <div style={{ fontSize: '3rem', color: 'var(--accent)' }}>
            <i className='fa-solid fa-circle-check' aria-hidden='true' />
          </div>
          <p>Your change request has been successfully submitted.</p>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              background: 'var(--primary-light)',
              border: '1px solid rgba(22,99,255,0.15)',
              borderRadius: 'var(--radius-sm)',
              padding: '14px 32px',
              width: '100%',
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--ink-dim)',
              }}
            >
              Change ID
            </span>
            <span
              style={{
                fontSize: '1.5rem',
                fontWeight: 800,
                color: 'var(--primary)',
                fontFamily: 'monospace',
              }}
            >
              {newChangeId}
            </span>
          </div>
          <p style={{ color: 'var(--ink-dim)', fontSize: '0.875rem' }}>
            Stakeholder notifications have been sent automatically. You can
            track responses from your dashboard.
          </p>
        </div>
      </Modal>
    </Layout>
  );
}
